import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Tag, Alert, message } from 'antd';
import { PlusOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';

import { avaliacoesAdminService, modalidadeService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { TextArea } = Input;

// Grupos do modelo institucional (Fase 11): critérios GERAIS (Participação/
// Comportamento/Frequência) se aplicam a qualquer modalidade -- id_modalidade
// fica sempre NULL. DESENVOLVIMENTO_TECNICO é específico de UMA modalidade --
// id_modalidade é obrigatório nesse caso (mesma regra reforçada por CHECK no
// banco e por validação no schema Pydantic).
const GRUPOS_CRITERIO = [
  { value: 'PARTICIPACAO', label: 'Participação' },
  { value: 'COMPORTAMENTO', label: 'Comportamento' },
  { value: 'FREQUENCIA', label: 'Frequência' },
  { value: 'DESENVOLVIMENTO_TECNICO', label: 'Desenvolvimento Técnico' },
];

const GRUPO_LABEL = Object.fromEntries(GRUPOS_CRITERIO.map((g) => [g.value, g.label]));

// Contrato real: GET/POST/PUT /api/avaliacoes/criterios
// (CriterioAvaliacaoAdminResponse/Create/Update). Deliberadamente SEM
// DELETE -- este endpoint nunca existiu no backend (Fase 3): "remover" um
// critério da utilização futura é editá-lo para ativo=false. A ordenação
// já vem pronta do backend (ORDER BY ordem, id), nunca reordenada aqui.
const CriteriosAdminPainel = () => {
  const [criterios, setCriterios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const [modalidades, setModalidades] = useState([]);

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null); // registro sendo editado | null (criação)
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();
  const grupoSelecionado = Form.useWatch('grupo', form);

  const nomeModalidade = useCallback(
    (id) => modalidades.find((m) => m.id === id)?.nome_modalidade || `#${id}`,
    [modalidades],
  );

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesAdminService.listarCriterios();
      setCriterios(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar os critérios.');
      setErro(mensagem);
      setCriterios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    modalidadeService
      .list()
      .then((response) => setModalidades(Array.isArray(response.data) ? response.data : []))
      .catch(() => setModalidades([]));
  }, [carregar]);

  const abrirCriacao = () => {
    setEditando(null);
    form.resetFields();
    form.setFieldsValue({ ativo: true, ordem: 0, grupo: 'PARTICIPACAO' });
    setModalAberto(true);
  };

  const abrirEdicao = (registro) => {
    setEditando(registro);
    form.setFieldsValue(registro);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setEditando(null);
  };

  const handleSalvar = async (values) => {
    // Defesa em profundidade no frontend (a regra real é reforçada por CHECK
    // no banco e por validação no schema Pydantic): grupo geral nunca envia
    // id_modalidade, mesmo que o campo tenha ficado preenchido antes de trocar
    // o grupo.
    const payload = {
      ...values,
      id_modalidade: values.grupo === 'DESENVOLVIMENTO_TECNICO' ? values.id_modalidade : null,
    };
    setSalvando(true);
    try {
      if (editando) {
        await avaliacoesAdminService.atualizarCriterio(editando.id, payload);
        message.success('Critério atualizado com sucesso.');
      } else {
        await avaliacoesAdminService.criarCriterio(payload);
        message.success('Critério criado com sucesso.');
      }
      fecharModal();
      carregar();
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível salvar o critério.');
      message.error(mensagem);
    } finally {
      setSalvando(false);
    }
  };

  const columns = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'Descrição', dataIndex: 'descricao', key: 'descricao', ellipsis: true, render: (v) => v || '—' },
    {
      title: 'Grupo',
      dataIndex: 'grupo',
      key: 'grupo',
      width: 170,
      render: (grupo) => GRUPO_LABEL[grupo] || grupo,
    },
    {
      title: 'Modalidade',
      dataIndex: 'id_modalidade',
      key: 'id_modalidade',
      width: 150,
      render: (id) => (id ? nomeModalidade(id) : 'Todas as modalidades'),
    },
    { title: 'Ordem', dataIndex: 'ordem', key: 'ordem', width: 90 },
    {
      title: 'Status',
      dataIndex: 'ativo',
      key: 'ativo',
      width: 100,
      render: (ativo) => <Tag color={ativo ? 'green' : 'default'}>{ativo ? 'Ativo' : 'Inativo'}</Tag>,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 100,
      render: (_, registro) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => abrirEdicao(registro)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirCriacao}>
          Novo critério
        </Button>
      </div>

      {erro && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar"
          description={erro}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          }
        />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={erro ? [] : criterios}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: erro ? ' ' : 'Nenhum critério cadastrado.' }}
        scroll={{ x: 700 }}
      />

      <Modal
        title={editando ? 'Editar critério' : 'Novo critério'}
        open={modalAberto}
        onOk={() => form.submit()}
        onCancel={fecharModal}
        confirmLoading={salvando}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSalvar}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Nome é obrigatório' }]}>
            <Input placeholder="Ex: Participação, Autonomia..." />
          </Form.Item>
          <Form.Item name="descricao" label="Descrição">
            <TextArea placeholder="Descrição (opcional)" autoSize={{ minRows: 2, maxRows: 5 }} maxLength={1000} />
          </Form.Item>
          <Form.Item name="grupo" label="Grupo" rules={[{ required: true, message: 'Grupo é obrigatório' }]}>
            <Select
              options={GRUPOS_CRITERIO}
              onChange={(valor) => {
                if (valor !== 'DESENVOLVIMENTO_TECNICO') {
                  form.setFieldsValue({ id_modalidade: undefined });
                }
              }}
            />
          </Form.Item>
          {grupoSelecionado === 'DESENVOLVIMENTO_TECNICO' && (
            <Form.Item
              name="id_modalidade"
              label="Modalidade"
              rules={[{ required: true, message: 'Modalidade é obrigatória para critério técnico' }]}
            >
              <Select
                placeholder="Selecione a modalidade"
                options={modalidades.map((m) => ({ value: m.id, label: m.nome_modalidade }))}
              />
            </Form.Item>
          )}
          <Form.Item name="ordem" label="Ordem">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ativo" label="Ativo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="Critérios inativos permanecem no histórico, mas não entram em novos períodos."
          />
        </Form>
      </Modal>
    </div>
  );
};

export default CriteriosAdminPainel;
